import { Phone, Building2 } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useWaba } from '@/hooks/useWaba'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import type { WabaPhoneNumber } from '@/types'

// Group phones under their WABA account. Falls back to business_id, then the
// phone id, so grouping still works if the account id isn't present.
function accountKey(w: WabaPhoneNumber): string {
  return String(w.waba_account_id ?? w.business_id ?? w.id)
}

export function WabaSwitcher() {
  const { wabaList, selectedWaba, selectedWabaId, hasWaba, switchWaba, isLoading } =
    useWaba()

  if (!hasWaba || wabaList.length === 0) {
    return null
  }

  // Distinct WABA accounts for the first dropdown.
  const accountsMap = new Map<
    string,
    { key: string; name: string; businessId?: string }
  >()
  for (const w of wabaList) {
    const key = accountKey(w)
    if (!accountsMap.has(key)) {
      accountsMap.set(key, {
        key,
        name: w.business_name || w.waba_account_name || 'WABA account',
        businessId: w.business_id,
      })
    }
  }
  const accounts = Array.from(accountsMap.values())

  const currentAccountKey = selectedWaba ? accountKey(selectedWaba) : ''
  // Phone numbers belonging to the selected WABA account.
  const phonesForAccount = wabaList.filter(
    (w) => accountKey(w) === currentAccountKey,
  )

  // Switching WABA account jumps to that account's default/first phone number.
  const onSelectAccount = (key: string) => {
    const first =
      wabaList.find((w) => accountKey(w) === key && w.is_default) ||
      wabaList.find((w) => accountKey(w) === key)
    if (first) switchWaba(String(first.id))
  }

  return (
    <div className="flex min-w-0 items-center gap-1 sm:gap-2">
      {/* WABA account dropdown */}
      <Select
        value={currentAccountKey}
        onValueChange={onSelectAccount}
        disabled={isLoading || accounts.length <= 1}
      >
        <SelectTrigger
          className={cn('h-10 w-auto gap-1.5 px-2 sm:gap-2.5 sm:px-3 lg:min-w-[180px] lg:max-w-[260px] [&>span]:hidden lg:[&>span]:line-clamp-1')}
        >
          <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary-subtle text-primary">
            <Building2 className="h-3.5 w-3.5" />
          </div>
          <SelectValue placeholder="Select WABA">
            {selectedWaba
              ? selectedWaba.business_name ||
              selectedWaba.waba_account_name ||
              'WABA account'
              : 'Select WABA'}
          </SelectValue>
        </SelectTrigger>
        <SelectContent align="end" className="min-w-[min(260px,calc(100vw-2rem))] max-w-[calc(100vw-2rem)]">
          {accounts.map((a) => (
            <SelectItem
              key={a.key}
              value={a.key}
              className="py-2 pl-2.5 pr-9"
            >
              <div className="flex items-center gap-2.5">
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary-subtle text-primary">
                  <Building2 className="h-4 w-4" />
                </div>
                <div className="flex min-w-0 flex-col">
                  <span className="truncate text-sm font-semibold leading-tight">
                    {a.name}
                  </span>
                  {a.businessId && (
                    <span className="truncate text-[11px] leading-tight text-muted-foreground">
                      ID: {a.businessId}
                    </span>
                  )}
                </div>
              </div>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {/* Phone number dropdown (scoped to the selected WABA account) */}
      <Select
        value={selectedWabaId ?? ''}
        onValueChange={switchWaba}
        disabled={isLoading || phonesForAccount.length <= 1}
      >
        <SelectTrigger
          className={cn('h-10 w-auto gap-1.5 px-2 sm:gap-2.5 sm:px-3 lg:min-w-[200px] lg:max-w-[280px] [&>span]:hidden lg:[&>span]:line-clamp-1')}
        >
          <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-success-subtle text-success-600">
            <Phone className="h-3.5 w-3.5" />
          </div>
          <SelectValue placeholder="Select number">
            {selectedWaba
              ? `${selectedWaba.display_phone_number} · ${selectedWaba.verified_name}`
              : 'Select number'}
          </SelectValue>
        </SelectTrigger>
        <SelectContent align="end" className="min-w-[min(260px,calc(100vw-2rem))] max-w-[calc(100vw-2rem)]">
          {phonesForAccount.map((waba) => (
            <SelectItem
              key={String(waba.id)}
              value={String(waba.id)}
              className="py-2 pl-2.5 pr-9"
            >
              <div className="flex items-center gap-2.5">
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-success-subtle text-success-600">
                  <Phone className="h-4 w-4" />
                </div>
                <div className="flex min-w-0 flex-col">
                  <span className="truncate text-sm font-semibold leading-tight">
                    {waba.display_phone_number}
                  </span>
                  <span className="truncate text-[11px] leading-tight text-muted-foreground">
                    {waba.verified_name}
                  </span>
                </div>
              </div>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  )
}
